import { cn } from '@/lib/utils';
import { formatISODateToDDMMYYYY } from '@/utils/transformer';
import { CircleArrowLeft, CircleArrowRight } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { E2E_SCHEDULING } from '@/constants/e2eSchedulingTestIds';

const MONTH_NAMES_PT = [
	'Janeiro',
	'Fevereiro',
	'Março',
	'Abril',
	'Maio',
	'Junho',
	'Julho',
	'Agosto',
	'Setembro',
	'Outubro',
	'Novembro',
	'Dezembro',
] as const;

function startOfMonth(date: Date): Date {
	return new Date(date.getFullYear(), date.getMonth(), 1);
}

function addMonths(date: Date, months: number): Date {
	return new Date(date.getFullYear(), date.getMonth() + months, 1);
}

function compareMonths(a: Date, b: Date): number {
	return (
		a.getFullYear() * 12 +
		a.getMonth() -
		(b.getFullYear() * 12 + b.getMonth())
	);
}

function parseYmdDate(value: string): Date | null {
	const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
	if (!match) return null;

	const year = Number(match[1]);
	const month = Number(match[2]) - 1;
	const day = Number(match[3]);
	const parsed = new Date(year, month, day);

	if (
		parsed.getFullYear() !== year ||
		parsed.getMonth() !== month ||
		parsed.getDate() !== day
	) {
		return null;
	}

	return parsed;
}

/** Valor do picker no formato dd/MM/yyyy (ex.: legenda e defaultValue). */
function parsePickerDateLabel(value: string): Date | null {
	const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value);
	if (!match) return null;

	const day = Number(match[1]);
	const month = Number(match[2]) - 1;
	const year = Number(match[3]);
	const parsed = new Date(year, month, day);

	if (
		parsed.getFullYear() !== year ||
		parsed.getMonth() !== month ||
		parsed.getDate() !== day
	) {
		return null;
	}

	return parsed;
}

function getSortedAvailableMonths(availableDates: string[]): Date[] {
	const uniqueMonths = new Map<string, Date>();

	for (const dateValue of availableDates) {
		const parsed = parseYmdDate(dateValue);
		if (!parsed) continue;

		const monthStart = startOfMonth(parsed);
		const key = `${monthStart.getFullYear()}-${monthStart.getMonth()}`;
		uniqueMonths.set(key, monthStart);
	}

	return Array.from(uniqueMonths.values()).sort((a, b) =>
		compareMonths(a, b),
	);
}

interface AvailableDatePickerProps {
	availableDates: string[];
	onDateChange: (date: string | null) => void;
	loading?: boolean;
	defaultValue?: string | null;
	disabled?: boolean;
	fullWidth?: boolean;
	/** Impede navegar para meses anteriores ao mês atual. */
	lockPastMonths?: boolean;
	/** Quantos meses à frente do atual o usuário pode navegar (ex.: 1 = só o próximo mês). */
	maxMonthsAhead?: number;
	/** Abre no primeiro mês com dias disponíveis se o mês atual estiver vazio. */
	autoFocusFirstAvailableMonth?: boolean;
	/** Qualquer dia futuro (hoje em diante) é clicável, sem depender de `availableDates`. */
	allowAnyFutureDay?: boolean;
	/** Qualquer dia do mês visível é clicável (inclui passado) — fluxo PRO. */
	allowAnyDay?: boolean;
	/** Layout reduzido para colunas estreitas (ex.: agenda PRO em ~1200px). */
	compact?: boolean;
}

const AvailableDatePicker = ({
	availableDates,
	onDateChange,
	loading = false,
	defaultValue = null,
	disabled = false,
	fullWidth = false,
	lockPastMonths = false,
	maxMonthsAhead,
	autoFocusFirstAvailableMonth = false,
	allowAnyFutureDay = false,
	allowAnyDay = false,
	compact = false,
}: AvailableDatePickerProps) => {
	const dayCellHeightClass = compact
		? 'h-7'
		: fullWidth
		  ? 'h-10 md:h-11'
		  : 'h-9 md:h-10';
	const dayCellWidthClass = compact
		? 'w-full min-w-0'
		: fullWidth
		  ? 'w-full min-w-0 md:w-12'
		  : 'w-9 md:w-10';
	const dayGridGapClass = compact
		? 'gap-x-0.5 gap-y-1'
		: fullWidth
		  ? 'gap-x-1 gap-y-2 md:gap-x-3 md:gap-y-2.5'
		  : 'gap-1.5 md:gap-2';
	const dayGridLayoutClass =
		compact || fullWidth ? 'justify-items-stretch' : 'justify-items-center';

	const todayMonth = useMemo(() => startOfMonth(new Date()), []);
	const todayDate = useMemo(() => {
		const today = new Date();
		today.setHours(0, 0, 0, 0);
		return today;
	}, []);
	const availableMonths = useMemo(
		() => getSortedAvailableMonths(availableDates),
		[availableDates],
	);

	const minNavigableMonth = lockPastMonths ? todayMonth : null;

	const maxNavigableMonth = useMemo(() => {
		if (!lockPastMonths || maxMonthsAhead === undefined) return null;

		const defaultMaxMonth = addMonths(todayMonth, maxMonthsAhead);
		const futureAvailableMonths = availableMonths.filter(
			(month) => compareMonths(month, todayMonth) >= 0,
		);
		const furthestAvailableMonth =
			futureAvailableMonths[futureAvailableMonths.length - 1] ?? null;

		if (
			furthestAvailableMonth &&
			compareMonths(furthestAvailableMonth, defaultMaxMonth) > 0
		) {
			return furthestAvailableMonth;
		}

		return defaultMaxMonth;
	}, [availableMonths, lockPastMonths, maxMonthsAhead, todayMonth]);

	const firstAvailableMonthFromToday = useMemo(() => {
		return (
			availableMonths.find(
				(month) => compareMonths(month, todayMonth) >= 0,
			) ?? todayMonth
		);
	}, [availableMonths, todayMonth]);

	const clampViewMonth = useCallback(
		(month: Date) => {
			let clamped = startOfMonth(month);
			if (
				minNavigableMonth &&
				compareMonths(clamped, minNavigableMonth) < 0
			) {
				clamped = minNavigableMonth;
			}
			if (
				maxNavigableMonth &&
				compareMonths(clamped, maxNavigableMonth) > 0
			) {
				clamped = maxNavigableMonth;
			}
			return clamped;
		},
		[minNavigableMonth, maxNavigableMonth],
	);

	const [viewMonth, setViewMonth] = useState(() =>
		clampViewMonth(todayMonth),
	);
	const [isSelectedDate, setIsSelectedDate] = useState<Date | null>(null);
	const hasAutoFocusedMonthRef = useRef(false);
	const lastAvailabilitySignatureRef = useRef('');

	const canGoToPreviousMonth =
		!minNavigableMonth || compareMonths(viewMonth, minNavigableMonth) > 0;
	const canGoToNextMonth =
		!maxNavigableMonth || compareMonths(viewMonth, maxNavigableMonth) < 0;

	useEffect(() => {
		const availabilitySignature = availableDates.join('|');
		if (lastAvailabilitySignatureRef.current !== availabilitySignature) {
			lastAvailabilitySignatureRef.current = availabilitySignature;
			hasAutoFocusedMonthRef.current = false;
		}
	}, [availableDates]);

	useEffect(() => {
		if (
			loading ||
			!autoFocusFirstAvailableMonth ||
			!lockPastMonths ||
			hasAutoFocusedMonthRef.current
		) {
			return;
		}

		if (availableDates.length === 0) {
			hasAutoFocusedMonthRef.current = true;
			return;
		}

		const currentMonthHasAvailability = availableDates.some((dateValue) => {
			const parsed = parseYmdDate(dateValue);
			return (
				parsed && compareMonths(startOfMonth(parsed), todayMonth) === 0
			);
		});

		if (!currentMonthHasAvailability) {
			setViewMonth(clampViewMonth(firstAvailableMonthFromToday));
		}

		hasAutoFocusedMonthRef.current = true;
	}, [
		autoFocusFirstAvailableMonth,
		availableDates,
		clampViewMonth,
		firstAvailableMonthFromToday,
		loading,
		lockPastMonths,
		todayMonth,
	]);

	useEffect(() => {
		if (!defaultValue) {
			setIsSelectedDate(null);
			return;
		}

		const defaultDate =
			parsePickerDateLabel(defaultValue) ?? parseYmdDate(defaultValue);
		if (defaultDate) {
			setIsSelectedDate(defaultDate);
			setViewMonth(clampViewMonth(startOfMonth(defaultDate)));
		}
	}, [clampViewMonth, defaultValue]);

	const monthYearHeader = useMemo(() => {
		const monthTitle = MONTH_NAMES_PT[viewMonth.getMonth()];
		const y = viewMonth.getFullYear();
		return `${monthTitle} ${y}`;
	}, [viewMonth]);

	const isDateAvailable = (date: Date) => {
		if (loading) return false;
		// Comparação por data LOCAL para não sofrer deslocamento de fuso horário.
		const formatLocalDate = (d: Date) =>
			`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
				2,
				'0',
			)}-${String(d.getDate()).padStart(2, '0')}`;
		const dateStr = formatLocalDate(date);
		if (allowAnyDay) return true;
		if (allowAnyFutureDay) {
			const today = new Date();
			today.setHours(0, 0, 0, 0);
			const candidate = new Date(date);
			candidate.setHours(0, 0, 0, 0);
			return candidate >= today;
		}
		return availableDates.includes(dateStr);
	};

	const isSameDay = (date1: Date | null, date2: Date | null) => {
		if (!date1 || !date2) return false;
		return (
			date1.getFullYear() === date2.getFullYear() &&
			date1.getMonth() === date2.getMonth() &&
			date1.getDate() === date2.getDate()
		);
	};

	const renderWeekdayNames = () => {
		const weekdays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
		return weekdays.map((day) => (
			<div
				key={day}
				className={cn(
					'text-center font-medium text-zinc-500',
					compact ? 'text-[10px]' : 'text-sm md:text-base',
				)}
			>
				{loading ? (
					<div className="h-5 w-full animate-pulse rounded bg-zinc-200" />
				) : (
					day
				)}
			</div>
		));
	};

	const renderDays = () => {
		if (loading) {
			return Array.from({ length: 30 }).map((_, i) => (
				<div
					key={`skeleton-${i}`}
					className={cn(
						'animate-pulse rounded-md bg-zinc-200',
						dayCellHeightClass,
						dayCellWidthClass,
					)}
				/>
			));
		}

		const viewYear = viewMonth.getFullYear();
		const viewMonthIndex = viewMonth.getMonth();
		const daysInMonth = new Date(viewYear, viewMonthIndex + 1, 0).getDate();
		const firstDayOfMonth = new Date(viewYear, viewMonthIndex, 1).getDay();
		const days = [];

		for (let i = 0; i < firstDayOfMonth; i++) {
			days.push(
				<div
					key={`empty-${i}`}
					className={cn(dayCellHeightClass, dayCellWidthClass)}
				/>,
			);
		}

		for (let day = 1; day <= daysInMonth; day++) {
			const date = new Date(viewYear, viewMonthIndex, day);
			const available = isDateAvailable(date);
			const isChosen = isSameDay(isSelectedDate, date);
			const isToday = isSameDay(todayDate, date);

			days.push(
				<button
					key={day}
					type="button"
					data-testid={
						available
							? `${E2E_SCHEDULING.DAY_PREFIX}${day}`
							: undefined
					}
					onClick={() => {
						if (available) {
							onDateChange(
								formatISODateToDDMMYYYY(date.toDateString()),
							);
							setIsSelectedDate(date);
						}
					}}
					disabled={!available || disabled}
					className={cn(
						'relative flex items-center justify-center rounded-md font-semibold transition-colors',
						compact ? 'text-xs' : 'text-sm md:text-base',
						dayCellHeightClass,
						dayCellWidthClass,
						!available && 'cursor-not-allowed text-zinc-400',
						available &&
							!isChosen &&
							'cursor-pointer bg-brand-orange/90 text-white hover:bg-[color-mix(in_srgb,theme(colors.brand-orange),black_18%)]',
						available &&
							isChosen &&
							'cursor-pointer bg-stone-900 text-white hover:bg-stone-900',
					)}
				>
					{day}
					{isToday ? (
						<span
							className={cn(
								'pointer-events-none absolute bottom-1.5 left-1/2 size-1 -translate-x-1/2 rounded-full',
								available ? 'bg-white' : 'bg-zinc-400',
							)}
							aria-hidden
						/>
					) : null}
				</button>,
			);
		}

		return days;
	};

	return (
		<div
			className={cn(
				'mx-auto w-full space-y-2 bg-inherit',
				fullWidth ? 'max-w-none' : 'max-w-lg',
			)}
			data-testid={E2E_SCHEDULING.DATE_PICKER_ROOT}
		>
			<div
				className={cn(
					'w-full',
					compact
						? 'rounded-xl border border-zinc-100 bg-white p-3 shadow-sm'
						: fullWidth
						  ? 'px-1 py-2 md:rounded-2xl md:border md:border-zinc-100 md:bg-white md:p-6 md:shadow-sm'
						  : 'rounded-2xl border border-zinc-100 bg-white p-5 shadow-sm md:p-6',
				)}
			>
				<div
					className={cn(
						'flex items-center justify-between gap-2',
						compact ? 'mb-3' : 'mb-5',
					)}
				>
					<button
						type="button"
						onClick={() =>
							setViewMonth((prev) =>
								clampViewMonth(addMonths(prev, -1)),
							)
						}
						disabled={loading || disabled || !canGoToPreviousMonth}
						aria-label="Mês anterior"
					>
						{loading ? (
							<div className="h-5 w-5 animate-pulse rounded-full bg-zinc-200" />
						) : (
							<CircleArrowLeft
								className={cn(
									compact ? 'size-5' : 'size-6 md:size-7',
									canGoToPreviousMonth
										? 'text-zinc-400'
										: 'text-zinc-200',
								)}
							/>
						)}
					</button>

					<h3
						className={cn(
							'font-semibold leading-5 text-dark',
							compact ? 'text-sm' : 'text-md md:text-lg',
						)}
					>
						{loading ? (
							<div className="h-6 w-36 animate-pulse rounded bg-zinc-200 md:h-7" />
						) : (
							monthYearHeader
						)}
					</h3>

					<button
						type="button"
						onClick={() =>
							setViewMonth((prev) =>
								clampViewMonth(addMonths(prev, 1)),
							)
						}
						disabled={loading || disabled || !canGoToNextMonth}
						aria-label="Próximo mês"
					>
						{loading ? (
							<div className="h-5 w-5 animate-pulse rounded-full bg-zinc-200" />
						) : (
							<CircleArrowRight
								className={cn(
									compact ? 'size-5' : 'size-6 md:size-7',
									canGoToNextMonth
										? 'text-zinc-400'
										: 'text-zinc-200',
								)}
							/>
						)}
					</button>
				</div>
				<div
					className={cn(
						'grid w-full min-w-0 grid-cols-7',
						compact ? 'mt-2' : 'mt-3',
						dayGridLayoutClass,
						dayGridGapClass,
					)}
				>
					{renderWeekdayNames()}
				</div>
				<div
					className={cn(
						'grid w-full min-w-0 grid-cols-7',
						dayGridLayoutClass,
						dayGridGapClass,
					)}
				>
					{renderDays()}
				</div>
			</div>
			<div
				className={cn(
					'flex flex-wrap items-center justify-center gap-x-5 gap-y-2 px-1 pt-2 text-zinc-500',
					compact ? 'hidden' : 'text-[11px] md:text-xs',
				)}
			>
				<div className="flex items-center gap-1.5">
					<div
						className={cn(
							'size-3.5 shrink-0 rounded',
							loading
								? 'animate-pulse bg-zinc-200'
								: 'bg-brand-orange/90',
						)}
					/>
					<span>
						{loading ? (
							<div className="h-3.5 w-20 animate-pulse rounded bg-zinc-200" />
						) : (
							'Dias disponíveis'
						)}
					</span>
				</div>
				<div className="flex items-center gap-1.5">
					<div
						className={cn(
							'size-3.5 shrink-0 rounded',
							loading
								? 'animate-pulse bg-zinc-200'
								: 'bg-stone-900',
						)}
					/>
					<span>
						{loading ? (
							<div className="h-3.5 w-16 animate-pulse rounded bg-zinc-200" />
						) : (
							'Dia escolhido'
						)}
					</span>
				</div>
			</div>
		</div>
	);
};

export { AvailableDatePicker };
